/**
 * AudiobookDetailView - Audiobook detail panel with tabs
 *
 * Single Responsibility: Displays audiobook details with tabbed navigation.
 * Similar to PodcastDetailView but tailored for audiobooks.
 */
package com.castly.admin.ui.audiobooks

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.*
import com.castly.admin.ui.components.ConfirmDialog
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

enum class AudiobookDetailTab {
    DATA,
    CHAPTERS,
    TAGS
}

@Composable
fun AudiobookDetailView(
    audiobook: AudiobookDto,
    activeTab: AudiobookDetailTab,
    apiClient: ApiClient,
    onTabChange: (AudiobookDetailTab) -> Unit,
    onAudiobookUpdate: (String) -> Unit,
    onClose: () -> Unit,
    onShowEpisodeImport: () -> Unit,
    onEdit: () -> Unit = {}
) {
    val colors = AppTheme.colors
    val userRoleStr = window.localStorage.getItem("user_role") ?: "NONE"
    val isAdmin = userRoleStr.uppercase() == "ADMIN" || userRoleStr.uppercase() == "SUPER_ADMIN"
    val canEdit = userRoleStr.uppercase() in setOf("LABEL_EDITOR", "EDITOR", "ADMIN", "SUPER_ADMIN")
    val canPublish = userRoleStr.uppercase() in setOf("EDITOR", "ADMIN", "SUPER_ADMIN")
    val canDelete = userRoleStr.uppercase() in setOf("ADMIN", "SUPER_ADMIN")
    var showMenu by remember { mutableStateOf(false) }
    var isUploadingImage by remember { mutableStateOf(false) }
    var uploadImageError by remember { mutableStateOf<String?>(null) }
    val isSuperAdmin = userRoleStr.uppercase() == "SUPER_ADMIN"
    var showDeleteConfirm by remember { mutableStateOf(false) }
    var isDeleting by remember { mutableStateOf(false) }
    var deleteError by remember { mutableStateOf<String?>(null) }
    var isRestoring by remember { mutableStateOf(false) }
    var showHardDeleteConfirm by remember { mutableStateOf(false) }
    var isHardDeleting by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val isHidden = audiobook.deletedAt != null

    Div({
        attr("data-testid", "audiobook-detail-panel")
        style {
            height(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            themeTransition()
        }
    }) {
        // Header with title, image, and close button
        Div({
            style {
                padding(Spacing.lg)
                property("border-bottom", "1px solid ${colors.border}")
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                flexShrink(0)
            }
        }) {
            // Left side: Image + Title + Metadata
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.md)
                    alignItems(AlignItems.Start)
                    flex("1")
                }
            }) {
                // Cover Image with upload controls
                val displayImageUrl = audiobook.coverImageUrl
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        property("flex-direction", "column")
                        gap(Spacing.xs)
                    }
                }) {
                    // Image
                    Div({
                        style {
                            position(Position.Relative)
                        }
                    }) {
                        if (displayImageUrl != null) {
                            Img(displayImageUrl) {
                                style {
                                    width(56.px)
                                    height(56.px)
                                    borderRadius(BorderRadius.lg)
                                    property("object-fit", "cover")
                                    border(1.px, LineStyle.Solid, Color(colors.border))
                                }
                            }
                        } else {
                            Div({
                                style {
                                    width(56.px)
                                    height(56.px)
                                    backgroundColor(Color(colors.surfaceHover))
                                    borderRadius(BorderRadius.lg)
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    justifyContent(JustifyContent.Center)
                                    color(Color(colors.textMuted))
                                    fontSize(10.px)
                                    border(1.px, LineStyle.Solid, Color(colors.border))
                                }
                            }) { Text("No Image") }
                        }

                        // Hidden file input for image upload
                        org.jetbrains.compose.web.dom.Input(org.jetbrains.compose.web.attributes.InputType.File) {
                            id("audiobook-cover-upload")
                            attr("accept", "image/*")
                            if (isUploadingImage) disabled()
                            onChange { event ->
                                val input = event.target as org.w3c.dom.HTMLInputElement
                                val file = input.files?.item(0)
                                if (file != null) {
                                    scope.launch {
                                        isUploadingImage = true
                                        uploadImageError = null
                                        try {
                                            apiClient.uploadAudiobookCoverImage(audiobook.id, file)
                                            input.value = ""
                                            onAudiobookUpdate(audiobook.id)
                                        } catch (e: Exception) {
                                            uploadImageError = e.message ?: "Upload failed"
                                        } finally {
                                            isUploadingImage = false
                                        }
                                    }
                                }
                            }
                            style {
                                display(DisplayStyle.None)
                            }
                        }
                    }

                    // Image controls
                    if (canEdit) Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(Spacing.xs)
                        }
                    }) {
                        // Change Image button
                        Button({
                            onClick {
                                kotlinx.browser.document.getElementById("audiobook-cover-upload")?.asDynamic()?.click()
                            }
                            if (isUploadingImage) disabled()
                            style {
                                padding(3.px, 6.px)
                                backgroundColor(Color(colors.primary))
                                color(Color("#ffffff"))
                                border(0.px)
                                borderRadius(BorderRadius.sm)
                                fontSize(10.px)
                                fontWeight("500")
                                cursor(if (isUploadingImage) "not-allowed" else "pointer")
                                property("white-space", "nowrap")
                                if (isUploadingImage) opacity(0.6)
                            }
                        }) { Text(if (isUploadingImage) "..." else "Change") }
                    }
                }

                // Title and metadata
                Div({
                    style {
                        flex("1")
                    }
                }) {
                    H3({
                        style {
                            margin(0.px)
                            fontSize(20.px)
                            fontWeight("600")
                        }
                    }) { Text(audiobook.title) }

                    // Author and reader
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(Spacing.sm)
                            marginTop(Spacing.xs)
                            fontSize(13.px)
                            color(Color(colors.textMuted))
                        }
                    }) {
                        Span { Text(audiobook.authorName ?: "Unknown Author") }
                        if (audiobook.readerName != null) {
                            Span { Text("•") }
                            Span { Text("Read by ${audiobook.readerName}") }
                        }
                    }

                    // Status badge
                    Div({
                        style {
                            marginTop(Spacing.xs)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(Spacing.sm)
                        }
                    }) {
                        val statusColor = when (audiobook.status) {
                            "PUBLISHED" -> colors.success
                            "DRAFT" -> "#f59e0b"
                            "ARCHIVED" -> colors.textMuted
                            else -> colors.textMuted
                        }
                        Span({
                            style {
                                fontSize(11.px)
                                fontWeight("600")
                                padding(2.px, 8.px)
                                borderRadius(10.px)
                                backgroundColor(Color("${statusColor}20"))
                                color(Color(statusColor))
                                property("text-transform", "uppercase")
                                property("letter-spacing", "0.5px")
                            }
                        }) {
                            Text(audiobook.status)
                        }

                        if (audiobook.status == "DRAFT" && canPublish) {
                            var isPublishing by remember { mutableStateOf(false) }
                            Button({
                                onClick {
                                    scope.launch {
                                        isPublishing = true
                                        try {
                                            apiClient.publishAudiobook(audiobook.id)
                                            onAudiobookUpdate(audiobook.id)
                                        } catch (e: Exception) {
                                            kotlinx.browser.window.alert("Failed to publish: ${e.message}")
                                        } finally {
                                            isPublishing = false
                                        }
                                    }
                                }
                                if (isPublishing) disabled()
                                style {
                                    padding(2.px, 10.px)
                                    fontSize(11.px)
                                    fontWeight("600")
                                    backgroundColor(Color(colors.success))
                                    color(Color("#ffffff"))
                                    border(0.px)
                                    borderRadius(10.px)
                                    cursor(if (isPublishing) "not-allowed" else "pointer")
                                }
                            }) {
                                Text(if (isPublishing) "Publishing..." else "Publish")
                            }
                        }
                    }

                    // Incomplete data warning
                    val missingFields = mutableListOf<String>()
                    if (audiobook.isbn.isNullOrBlank()) missingFields.add("ISBN")
                    if (audiobook.authorName.isNullOrBlank()) missingFields.add("Author")
                    if (missingFields.isNotEmpty()) {
                        Div({
                            style {
                                marginTop(Spacing.xs)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(Spacing.xs)
                                padding(Spacing.xs, Spacing.sm)
                                backgroundColor(Color(if (colors == LightColors) "#fef3c7" else "#422006"))
                                border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fcd34d" else "#92400e"))
                                borderRadius(BorderRadius.sm)
                                fontSize(Typography.xs)
                                color(Color(if (colors == LightColors) "#92400e" else "#fcd34d"))
                            }
                        }) {
                            Text("Missing: ${missingFields.joinToString(", ")}")
                        }
                    }

                    // Show upload error
                    uploadImageError?.let {
                        Div({
                            style {
                                marginTop(Spacing.sm)
                                padding(Spacing.sm)
                                backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                                border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fca5a5" else "#991b1b"))
                                borderRadius(BorderRadius.sm)
                                fontSize(Typography.sm)
                                color(Color(colors.danger))
                                themeTransition()
                            }
                        }) {
                            Text(it)
                            Button({
                                onClick { uploadImageError = null }
                                style {
                                    marginLeft(Spacing.sm)
                                    padding(2.px, 6.px)
                                    backgroundColor(Color("transparent"))
                                    border(0.px)
                                    color(Color(colors.danger))
                                    cursor("pointer")
                                    fontSize(Typography.sm)
                                    fontWeight("600")
                                }
                            }) { Text("×") }
                        }
                    }

                    // Delete error
                    deleteError?.let {
                        Div({
                            style {
                                marginTop(Spacing.sm)
                                padding(Spacing.sm)
                                backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                                border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fca5a5" else "#991b1b"))
                                borderRadius(BorderRadius.sm)
                                fontSize(Typography.sm)
                                color(Color(colors.danger))
                                themeTransition()
                            }
                        }) {
                            Text(it)
                            Button({
                                onClick { deleteError = null }
                                style {
                                    marginLeft(Spacing.sm)
                                    padding(2.px, 6.px)
                                    backgroundColor(Color("transparent"))
                                    border(0.px)
                                    color(Color(colors.danger))
                                    cursor("pointer")
                                    fontSize(Typography.sm)
                                    fontWeight("600")
                                }
                            }) { Text("×") }
                        }
                    }
                }
            }

            // Actions area
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(Spacing.sm)
                }
            }) {
                // Close button
                Button({
                    onClick { onClose() }
                    attr("data-testid", "close-detail-button")
                    style {
                        padding(Spacing.sm, Spacing.md)
                        backgroundColor(Color(colors.surfaceHover))
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor("pointer")
                        fontSize(Typography.xl)
                        fontWeight("700")
                    }
                }) { Text("×") }

                // Three-dots menu
                Div({
                    style {
                        position(Position.Relative)
                    }
                }) {
                    Button({
                        onClick { showMenu = !showMenu }
                        style {
                            padding(Spacing.sm, Spacing.md)
                            backgroundColor(Color(colors.surfaceHover))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                            fontSize(20.px)
                            fontWeight("700")
                        }
                    }) { Text("⋮") }

                    if (showMenu) {
                        Div({
                            style {
                                position(Position.Absolute)
                                top(100.percent)
                                right(0.px)
                                marginTop(Spacing.xs)
                                backgroundColor(Color(colors.surface))
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                borderRadius(BorderRadius.md)
                                property("box-shadow", "0 4px 6px rgba(0, 0, 0, 0.1)")
                                property("z-index", "10")
                                minWidth(180.px)
                                themeTransition()
                            }
                        }) {
                            if (canEdit) {
                                MenuButton("Edit", onClick = {
                                    showMenu = false
                                    onEdit()
                                })
                                MenuButton("Change Image", onClick = {
                                    showMenu = false
                                    kotlinx.browser.document.getElementById("audiobook-cover-upload")?.asDynamic()?.click()
                                })
                            }
                            if (canDelete) {
                                if (isHidden) {
                                    MenuButton("Restore", onClick = {
                                        showMenu = false
                                        scope.launch {
                                            isRestoring = true
                                            try {
                                                apiClient.restoreAudiobook(audiobook.id)
                                                onAudiobookUpdate(audiobook.id)
                                            } catch (e: Exception) {
                                                deleteError = e.message ?: "Failed to restore audiobook"
                                            } finally {
                                                isRestoring = false
                                            }
                                        }
                                    })
                                    if (isSuperAdmin) {
                                        MenuButton("Permanently Delete", onClick = {
                                            showMenu = false
                                            showHardDeleteConfirm = true
                                        })
                                    }
                                } else {
                                    MenuButton("Delete", onClick = {
                                        showMenu = false
                                        showDeleteConfirm = true
                                    })
                                }
                            }
                        }
                    }
                }
            }
        }

        // Hidden audiobook banner
        if (isHidden) {
            Div({
                style {
                    padding(Spacing.sm, Spacing.lg)
                    backgroundColor(Color(if (colors == LightColors) "#fef3c7" else "#422006"))
                    color(Color(if (colors == LightColors) "#92400e" else "#fcd34d"))
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.SpaceBetween)
                    fontSize(13.px)
                    fontWeight("600")
                }
            }) {
                Span { Text("This audiobook is hidden from the client API") }
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(8.px)
                    }
                }) {
                    if (canDelete) Button({
                        onClick {
                            scope.launch {
                                isRestoring = true
                                try {
                                    apiClient.restoreAudiobook(audiobook.id)
                                    onAudiobookUpdate(audiobook.id)
                                } catch (e: Exception) {
                                    deleteError = e.message ?: "Failed to restore"
                                } finally {
                                    isRestoring = false
                                }
                            }
                        }
                        if (isRestoring) disabled()
                        style {
                            padding(4.px, 12.px)
                            backgroundColor(Color(colors.success))
                            color(Color("#ffffff"))
                            border(0.px)
                            borderRadius(BorderRadius.sm)
                            cursor(if (isRestoring) "not-allowed" else "pointer")
                            fontSize(12.px)
                            fontWeight("600")
                        }
                    }) { Text(if (isRestoring) "Restoring..." else "Restore") }
                    if (isSuperAdmin) Button({
                        onClick { showHardDeleteConfirm = true }
                        style {
                            padding(4.px, 12.px)
                            backgroundColor(Color("#dc2626"))
                            color(Color("#ffffff"))
                            border(0.px)
                            borderRadius(BorderRadius.sm)
                            cursor("pointer")
                            fontSize(12.px)
                            fontWeight("600")
                        }
                    }) { Text("Permanently Delete") }
                }
            }
        }

        // Tabs
        Div({
            attr("data-testid", "audiobook-detail-tabs")
            style {
                display(DisplayStyle.Flex)
                property("border-bottom", "1px solid ${colors.border}")
                backgroundColor(Color(colors.surfaceHover))
                flexShrink(0)
                themeTransition()
            }
        }) {
            TabButton("Data", activeTab == AudiobookDetailTab.DATA) {
                onTabChange(AudiobookDetailTab.DATA)
            }
            TabButton("Chapters", activeTab == AudiobookDetailTab.CHAPTERS) {
                onTabChange(AudiobookDetailTab.CHAPTERS)
            }
            TabButton("Tags", activeTab == AudiobookDetailTab.TAGS) {
                onTabChange(AudiobookDetailTab.TAGS)
            }
        }

        // Download progress (for RSS-imported audiobooks with pending downloads)
        Div({
            style {
                padding(Spacing.lg, Spacing.lg, 0.px, Spacing.lg)
            }
        }) {
            DownloadProgressPanel(apiClient = apiClient, audiobookId = audiobook.id)
        }

        // Tab content
        Div({
            style {
                flex("1")
                minHeight(0.px)
                property("overflow", "auto")
                padding(Spacing.lg)
            }
        }) {
            when (activeTab) {
                AudiobookDetailTab.DATA -> AudiobookDataTab(audiobook, apiClient) { onAudiobookUpdate(audiobook.id) }
                AudiobookDetailTab.CHAPTERS -> AudiobookEpisodesTab(audiobook, apiClient, onShowEpisodeImport) { onAudiobookUpdate(audiobook.id) }
                AudiobookDetailTab.TAGS -> AudiobookTagsTab(audiobook, apiClient)
            }
        }
    }

    // Delete confirmation dialog
    ConfirmDialog(
        isOpen = showDeleteConfirm,
        title = "Delete Audiobook",
        message = "Are you sure you want to delete \"${audiobook.title}\"? Any pending audio downloads will be cancelled. Existing audio files will be preserved.",
        confirmLabel = if (isDeleting) "Deleting..." else "Delete",
        onConfirm = {
            scope.launch {
                isDeleting = true
                deleteError = null
                try {
                    apiClient.deleteAudiobook(audiobook.id)
                    showDeleteConfirm = false
                    onClose()
                } catch (e: Exception) {
                    deleteError = e.message ?: "Failed to delete audiobook"
                } finally {
                    isDeleting = false
                }
            }
        },
        onCancel = {
            if (!isDeleting) {
                showDeleteConfirm = false
                deleteError = null
            }
        }
    )

    // Permanent delete confirmation dialog (super admin only)
    ConfirmDialog(
        isOpen = showHardDeleteConfirm,
        title = "Permanently Delete Audiobook",
        message = "WARNING: This will permanently delete \"${audiobook.title}\" and ALL associated data including episodes, audio files, and user progress. This action CANNOT be undone.",
        confirmLabel = if (isHardDeleting) "Deleting..." else "Permanently Delete",
        onConfirm = {
            scope.launch {
                isHardDeleting = true
                deleteError = null
                try {
                    apiClient.hardDeleteAudiobook(audiobook.id)
                    showHardDeleteConfirm = false
                    onClose()
                } catch (e: Exception) {
                    deleteError = e.message ?: "Failed to permanently delete audiobook"
                } finally {
                    isHardDeleting = false
                }
            }
        },
        onCancel = {
            if (!isHardDeleting) {
                showHardDeleteConfirm = false
                deleteError = null
            }
        }
    )
}

@Composable
fun AudiobookDataTab(
    audiobook: AudiobookDto,
    apiClient: ApiClient,
    onUpdate: () -> Unit
) {
    val colors = AppTheme.colors
    Div({
        style {
            display(DisplayStyle.Flex)
            property("flex-direction", "column")
            gap(Spacing.lg)
        }
    }) {
        // Title and Subtitle Section
        Div({
            style {
                backgroundColor(Color(colors.surfaceHover))
                borderRadius(BorderRadius.lg)
                padding(Spacing.lg)
                themeTransition()
            }
        }) {
            H3({
                style {
                    fontSize(Typography.lg)
                    fontWeight("600")
                    color(Color(colors.text))
                    marginTop(0.px)
                    marginBottom(Spacing.lg)
                }
            }) {
                Text("Title & Subtitle")
            }
            InfoRow("Title (Arabic)", audiobook.title)
            audiobook.titleEnglish?.let { englishTitle ->
                if (englishTitle.isNotBlank()) {
                    InfoRow("Title (English)", englishTitle)
                }
            }
            audiobook.subtitle?.let { subtitle ->
                if (subtitle.isNotBlank()) {
                    InfoRow("Subtitle (Arabic)", subtitle)
                }
            }
            audiobook.subtitleEnglish?.let { englishSubtitle ->
                if (englishSubtitle.isNotBlank()) {
                    InfoRow("Subtitle (English)", englishSubtitle)
                }
            }
        }

        // Metadata Section
        Div({
            style {
                backgroundColor(Color(colors.surfaceHover))
                borderRadius(BorderRadius.lg)
                padding(Spacing.lg)
                themeTransition()
            }
        }) {
            H3({
                style {
                    fontSize(Typography.lg)
                    fontWeight("600")
                    color(Color(colors.text))
                    marginTop(0.px)
                    marginBottom(Spacing.lg)
                }
            }) {
                Text("Details")
            }
            audiobook.isbn?.let { InfoRow("ISBN", it) }
            InfoRow("Author", audiobook.authorName ?: "Unknown")
            audiobook.readerName?.let { InfoRow("Reader", it) }
            audiobook.publisher?.let { InfoRow("Publisher", it) }
            audiobook.language?.let { InfoRow("Language", it) }
            audiobook.originalLanguage?.let { InfoRow("Original Language", it) }
            audiobook.copyrightYear?.let { InfoRow("Copyright Year", it.toString()) }
            audiobook.releaseDate?.let { InfoRow("Release Date", it) }
            audiobook.duration?.let { duration ->
                val hours = duration / 3600
                val minutes = (duration % 3600) / 60
                InfoRow("Duration", "${hours}h ${minutes}m")
            }
        }

        // Label Section
        LabelAssignmentSection(audiobook, apiClient, onUpdate)

        // Description
        audiobook.description?.let { description ->
            Div({
                style {
                    backgroundColor(Color(colors.surfaceHover))
                    borderRadius(BorderRadius.lg)
                    padding(Spacing.lg)
                    themeTransition()
                }
            }) {
                H4({
                    style {
                        fontSize(Typography.base)
                        fontWeight("600")
                        color(Color(colors.text))
                        marginTop(0.px)
                        marginBottom(Spacing.sm)
                    }
                }) {
                    Text("Description")
                }
                P({
                    style {
                        fontSize(Typography.base)
                        property("line-height", "1.6")
                        color(Color(colors.text))
                        margin(0.px)
                    }
                }) {
                    Text(description)
                }
            }
        }
    }
}

@Composable
fun AudiobookEpisodesTab(
    audiobook: AudiobookDto,
    apiClient: ApiClient,
    onShowEpisodeImport: () -> Unit,
    onUpdate: () -> Unit
) {
    val colors = AppTheme.colors
    var episodes by remember { mutableStateOf<List<AudiobookEpisodeDto>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var selectedEpisodeDetail by remember { mutableStateOf<AdminEpisodeDetailDto?>(null) }
    var isLoadingEpisodeDetail by remember { mutableStateOf(false) }
    var detailLoadError by remember { mutableStateOf<String?>(null) }
    var showBulkUploadPanel by remember { mutableStateOf(false) }
    var isRecalculating by remember { mutableStateOf(false) }
    var recalcMessage by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()
    val userRole = window.localStorage.getItem("user_role")?.uppercase() ?: "NONE"
    val isSuperAdmin = userRole == "SUPER_ADMIN"

    fun loadEpisodes() {
        scope.launch {
            isLoading = true
            errorMessage = null
            try {
                val response = apiClient.getAudiobookEpisodes(audiobook.id)
                episodes = response.episodes
            } catch (e: Exception) {
                errorMessage = e.message ?: "Failed to load episodes"
            } finally {
                isLoading = false
            }
        }
    }

    LaunchedEffect(audiobook.id) {
        loadEpisodes()
    }

    // Show bulk upload panel
    if (showBulkUploadPanel) {
        BulkEpisodeUploadPanel(
            audiobookId = audiobook.id,
            apiClient = apiClient,
            onComplete = {
                showBulkUploadPanel = false
                loadEpisodes()
                onUpdate()
            },
            onCancel = {
                showBulkUploadPanel = false
            }
        )
        return
    }

    // When an episode is selected, show the edit panel instead of the list
    if (selectedEpisodeDetail != null) {
        val episodeUserRole = window.localStorage.getItem("user_role") ?: "NONE"
        val episodeIsAdmin = episodeUserRole.uppercase() == "ADMIN" || episodeUserRole.uppercase() == "SUPER_ADMIN"
        EpisodeEditPanel(
            episode = selectedEpisodeDetail!!,
            apiClient = apiClient,
            isAdmin = episodeIsAdmin,
            onClose = { selectedEpisodeDetail = null },
            onEpisodeUpdated = {
                selectedEpisodeDetail = null
                loadEpisodes()
                onUpdate()
            }
        )
        return
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            property("flex-direction", "column")
            gap(Spacing.lg)
        }
    }) {
        // Header with import button
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            H3({
                style {
                    fontSize(Typography.xl)
                    fontWeight("600")
                    color(Color(colors.text))
                    margin(0.px)
                }
            }) {
                Text("Chapters (${episodes.size})")
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.sm)
                }
            }) {
                Button({
                    onClick { showBulkUploadPanel = true }
                    style {
                        padding(Spacing.sm, Spacing.lg)
                        fontSize(Typography.base)
                        fontWeight("600")
                        color(Color("#ffffff"))
                        backgroundColor(Color(colors.primary))
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor("pointer")
                        property("transition", "all 0.2s")
                    }
                }) {
                    Text("Add Episodes")
                }

                Button({
                    onClick { onShowEpisodeImport() }
                    style {
                        padding(Spacing.sm, Spacing.lg)
                        fontSize(Typography.base)
                        fontWeight("600")
                        color(Color(colors.text))
                        backgroundColor(Color(colors.surface))
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        cursor("pointer")
                        property("transition", "all 0.2s")
                    }
                }) {
                    Text("Import Chapters")
                }

                if (isSuperAdmin) {
                    Button({
                        onClick {
                            if (!isRecalculating) {
                                isRecalculating = true
                                recalcMessage = null
                                scope.launch {
                                    try {
                                        apiClient.recalculateAudiobookDurations(audiobook.id)
                                        recalcMessage = "Duration recalculation started. Reload in a moment to see updated values."
                                    } catch (e: Exception) {
                                        recalcMessage = "Failed: ${e.message}"
                                    } finally {
                                        isRecalculating = false
                                    }
                                }
                            }
                        }
                        if (isRecalculating) disabled()
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            fontSize(Typography.base)
                            fontWeight("600")
                            color(Color("#ffffff"))
                            backgroundColor(Color(if (isRecalculating) colors.textMuted else colors.warning))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor(if (isRecalculating) "not-allowed" else "pointer")
                            property("transition", "all 0.2s")
                        }
                    }) {
                        Text(if (isRecalculating) "Recalculating..." else "Recalculate Durations")
                    }
                }
            }
        }

        recalcMessage?.let { msg ->
            LaunchedEffect(msg) {
                kotlinx.coroutines.delay(5000)
                recalcMessage = null
            }
            Div({
                style {
                    padding(Spacing.sm, Spacing.lg)
                    backgroundColor(Color(if (msg.startsWith("Failed")) (if (colors == LightColors) "#fee2e2" else "#450a0a") else (if (colors == LightColors) "#f0fdf4" else "#052e16")))
                    borderRadius(BorderRadius.md)
                    color(Color(if (msg.startsWith("Failed")) colors.danger else colors.success))
                    fontSize(Typography.sm)
                }
            }) { Text(msg) }
        }

        if (isLoading || isLoadingEpisodeDetail) {
            Div({
                style {
                    textAlign("center")
                    padding(40.px)
                    color(Color(colors.textMuted))
                }
            }) {
                Text(if (isLoadingEpisodeDetail) "Loading chapter..." else "Loading chapters...")
            }
        }

        detailLoadError?.let { err ->
            Div({
                style {
                    backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                    border(1.px, LineStyle.Solid, Color(colors.danger))
                    borderRadius(BorderRadius.md)
                    padding(Spacing.sm, Spacing.md)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    themeTransition()
                }
            }) {
                Span({
                    style { fontSize(Typography.sm); color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5")) }
                }) { Text(err) }
                Button({
                    onClick { detailLoadError = null }
                    style { background("none"); border(0.px); cursor("pointer"); color(Color(colors.textMuted)); fontSize(Typography.lg) }
                }) { Text("×") }
            }
        }

        errorMessage?.let { error ->
            Div({
                style {
                    backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                    border(1.px, LineStyle.Solid, Color(colors.danger))
                    borderRadius(BorderRadius.md)
                    padding(Spacing.lg)
                    themeTransition()
                }
            }) {
                Div({
                    style {
                        fontSize(Typography.base)
                        fontWeight("600")
                        color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                        marginBottom(Spacing.sm)
                    }
                }) {
                    Text("Error")
                }
                Text(error)
            }
        }

        if (!isLoading && episodes.isEmpty()) {
            Div({
                style {
                    padding(40.px)
                    backgroundColor(Color(colors.surfaceHover))
                    borderRadius(BorderRadius.lg)
                    textAlign("center")
                    themeTransition()
                }
            }) {
                P({
                    style {
                        fontSize(Typography.base)
                        color(Color(colors.textMuted))
                        margin(0.px, 0.px, Spacing.lg)
                    }
                }) {
                    Text("No chapters yet. Import chapter files to get started.")
                }
            }
        } else if (!isLoading) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    property("flex-direction", "column")
                    gap(Spacing.sm)
                }
            }) {
                episodes.sortedBy { it.episodeNumber ?: 0 }.forEach { episode ->
                    Div({
                        onClick {
                            scope.launch {
                                isLoadingEpisodeDetail = true
                                detailLoadError = null
                                try {
                                    selectedEpisodeDetail = apiClient.getAudiobookEpisodeDetail(audiobook.id, episode.id)
                                } catch (e: Exception) {
                                    detailLoadError = e.message ?: "Failed to load episode detail"
                                } finally {
                                    isLoadingEpisodeDetail = false
                                }
                            }
                        }
                        style {
                            backgroundColor(Color(colors.surfaceHover))
                            borderRadius(BorderRadius.md)
                            padding(Spacing.md)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(Spacing.md)
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            cursor("pointer")
                            themeTransition()
                        }
                    }) {
                        // Episode image or number badge
                        if (episode.imageUrl != null) {
                            Img(episode.imageUrl!!) {
                                style {
                                    width(40.px)
                                    height(40.px)
                                    borderRadius(BorderRadius.md)
                                    property("object-fit", "cover")
                                    border(1.px, LineStyle.Solid, Color(colors.border))
                                    flexShrink(0)
                                }
                            }
                        } else {
                            Div({
                                style {
                                    width(40.px)
                                    height(40.px)
                                    backgroundColor(Color(colors.primary))
                                    color(Color("#ffffff"))
                                    borderRadius(BorderRadius.md)
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    justifyContent(JustifyContent.Center)
                                    fontWeight("600")
                                    fontSize(Typography.base)
                                    flexShrink(0)
                                }
                            }) {
                                Text((episode.episodeNumber ?: 0).toString())
                            }
                        }

                        // Episode details
                        Div({
                            style {
                                flex("1")
                            }
                        }) {
                            Div({
                                style {
                                    fontSize(Typography.base)
                                    fontWeight("600")
                                    color(Color(colors.text))
                                    marginBottom(Spacing.xs)
                                }
                            }) {
                                Text(episode.title)
                            }
                            Div({
                                style {
                                    fontSize(Typography.sm)
                                    color(Color(colors.textMuted))
                                }
                            }) {
                                val durationText = episode.duration?.let { duration ->
                                    val hours = duration / 3600
                                    val minutes = (duration % 3600) / 60
                                    val seconds = duration % 60
                                    "${hours}h ${minutes}m ${seconds}s"
                                } ?: "--"
                                Span { Text(durationText) }
                                Span { Text(" • ") }
                                episode.fileSize?.let { size ->
                                    val sizeMb = (size.toDouble() / 1024.0 / 1024.0).toInt()
                                    Span { Text("${sizeMb} MB") }
                                    Span { Text(" • ") }
                                }
                                Span { Text(episode.status) }
                            }
                        }

                        // Edit chevron
                        Span({
                            style {
                                fontSize(Typography.lg)
                                color(Color(colors.textMuted))
                                flexShrink(0)
                            }
                        }) { Text("›") }
                    }
                }
            }
        }
    }
}

@Composable
fun AudiobookTagsTab(
    audiobook: AudiobookDto,
    apiClient: ApiClient
) {
    val colors = AppTheme.colors
    var currentTags by remember { mutableStateOf<List<TagDto>>(emptyList()) }
    var allTags by remember { mutableStateOf<List<TagWithCountDto>>(emptyList()) }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var tagInput by remember { mutableStateOf("") }
    var showDropdown by remember { mutableStateOf(false) }
    var removeConfirmTag by remember { mutableStateOf<TagDto?>(null) }
    val scope = rememberCoroutineScope()

    fun loadTags() {
        scope.launch {
            loading = true
            error = null
            try {
                currentTags = apiClient.getAudiobookTags(audiobook.id)
                allTags = apiClient.listAudiobookTags()
            } catch (e: Exception) {
                error = "Failed to load tags: ${e.message}"
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(audiobook.id) {
        loadTags()
    }

    fun addTag(request: AddTagRequest) {
        scope.launch {
            try {
                val newTags = apiClient.addAudiobookTag(audiobook.id, request)
                currentTags = newTags
                tagInput = ""
                showDropdown = false
                allTags = apiClient.listAudiobookTags()
            } catch (e: Exception) {
                error = "Failed to add tag: ${e.message}"
            }
        }
    }

    fun removeTag(tagId: String) {
        scope.launch {
            try {
                apiClient.removeAudiobookTag(audiobook.id, tagId)
                currentTags = currentTags.filter { it.id != tagId }
                removeConfirmTag = null
                allTags = apiClient.listAudiobookTags()
            } catch (e: Exception) {
                error = "Failed to remove tag: ${e.message}"
            }
        }
    }

    val filteredTags = if (tagInput.isBlank()) {
        allTags
    } else {
        allTags.filter { it.name.contains(tagInput, ignoreCase = true) }
    }

    val alreadyAdded = currentTags.map { it.id }.toSet()

    val tagNamePattern = Regex("^[a-zA-Z0-9\\s\\-]*$")
    val isValidTagName = tagInput.isBlank() || (tagInput.length <= 50 && tagNamePattern.matches(tagInput))
    val validationError = when {
        tagInput.isBlank() -> null
        tagInput.length > 50 -> "Tag name must be 50 characters or less"
        !tagNamePattern.matches(tagInput) -> "Tag name can only contain letters, numbers, spaces, and hyphens"
        else -> null
    }

    Div({
        style {
            padding(Spacing.xl)
        }
    }) {
        H4({
            style {
                margin(0.px, 0.px, Spacing.lg, 0.px)
                fontSize(Typography.lg)
                fontWeight("600")
            }
        }) { Text("Tags") }

        error?.let { msg ->
            Div({
                style {
                    padding(Spacing.md)
                    backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                    color(Color(if (colors == LightColors) "#b91c1c" else "#fca5a5"))
                    borderRadius(BorderRadius.md)
                    marginBottom(Spacing.lg)
                    fontSize(13.px)
                    themeTransition()
                }
            }) { Text(msg) }
        }

        if (currentTags.isNotEmpty()) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexWrap(FlexWrap.Wrap)
                    gap(Spacing.sm)
                    marginBottom(Spacing.xl)
                }
            }) {
                currentTags.forEach { tag ->
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(6.px)
                            padding(6.px, Spacing.md)
                            backgroundColor(Color(colors.surfaceHover))
                            color(Color(colors.primary))
                            borderRadius(16.px)
                            fontSize(13.px)
                            fontWeight("500")
                        }
                    }) {
                        Text(tag.name)
                        Button({
                            onClick { removeConfirmTag = tag }
                            style {
                                background("none")
                                border(0.px)
                                color(Color(colors.primary))
                                cursor("pointer")
                                padding(0.px)
                                fontSize(Typography.base)
                                fontWeight("600")
                                lineHeight("1")
                            }
                        }) { Text("×") }
                    }
                }
            }
        }

        // Tag input with autocomplete
        Div({
            style {
                position(Position.Relative)
                marginBottom(Spacing.lg)
            }
        }) {
            Input(InputType.Text) {
                value(tagInput)
                onInput { tagInput = it.value }
                onFocus { showDropdown = true }
                onBlur {
                    scope.launch {
                        kotlinx.coroutines.delay(200)
                        showDropdown = false
                    }
                }
                placeholder("Add tags...")
                style {
                    width(100.percent)
                    padding(10.px, Spacing.md)
                    fontSize(Typography.base)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.md)
                    property("box-sizing", "border-box")
                    backgroundColor(Color(colors.surface))
                    color(Color(colors.text))
                    themeTransition()
                }
            }

            if (validationError != null) {
                Div({
                    style {
                        marginTop(Spacing.xs)
                        fontSize(Typography.sm)
                        color(Color(colors.danger))
                    }
                }) { Text(validationError) }
            }

            if (showDropdown && (filteredTags.isNotEmpty() || tagInput.isNotBlank())) {
                Div({
                    style {
                        position(Position.Absolute)
                        top(100.percent)
                        left(0.px)
                        right(0.px)
                        marginTop(Spacing.xs)
                        backgroundColor(Color(colors.surface))
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        property("box-shadow", "0 4px 6px rgba(0, 0, 0, 0.1)")
                        property("z-index", "10")
                        maxHeight(200.px)
                        property("overflow-y", "auto")
                        themeTransition()
                    }
                }) {
                    filteredTags.forEach { tag ->
                        if (tag.id !in alreadyAdded) {
                            Div({
                                onClick {
                                    addTag(AddTagRequest(tagId = tag.id))
                                }
                                style {
                                    padding(10.px, Spacing.md)
                                    cursor("pointer")
                                    fontSize(Typography.base)
                                    display(DisplayStyle.Flex)
                                    justifyContent(JustifyContent.SpaceBetween)
                                    alignItems(AlignItems.Center)
                                }
                                onMouseOver { it.target.asDynamic().style.backgroundColor = colors.surfaceHover }
                                onMouseOut { it.target.asDynamic().style.backgroundColor = "transparent" }
                            }) {
                                Span { Text(tag.name) }
                                Span({
                                    style {
                                        fontSize(Typography.sm)
                                        color(Color(colors.textMuted))
                                    }
                                }) { Text("(${tag.audiobookCount})") }
                            }
                        }
                    }

                    if (tagInput.isNotBlank() && filteredTags.all { it.name != tagInput } && isValidTagName) {
                        Div({
                            onClick {
                                addTag(AddTagRequest(name = tagInput))
                            }
                            style {
                                padding(10.px, Spacing.md)
                                cursor("pointer")
                                fontSize(Typography.base)
                                color(Color(colors.success))
                                fontWeight("500")
                            }
                            onMouseOver { it.target.asDynamic().style.backgroundColor = colors.surfaceHover }
                            onMouseOut { it.target.asDynamic().style.backgroundColor = "transparent" }
                        }) {
                            Text("Create \"$tagInput\"")
                        }
                    }
                }
            }
        }

        if (loading) {
            Div({
                style {
                    fontSize(13.px)
                    color(Color(colors.textMuted))
                    fontStyle("italic")
                }
            }) { Text("Loading...") }
        }
    }

    removeConfirmTag?.let { tag ->
        ConfirmationDialog(
            title = "Remove Tag?",
            message = "Are you sure you want to remove the tag \"${tag.name}\" from this audiobook?",
            onConfirm = { removeTag(tag.id) },
            onCancel = { removeConfirmTag = null }
        )
    }
}

@Composable
private fun TabButton(label: String, isActive: Boolean, onClick: () -> Unit) {
    val colors = AppTheme.colors
    Button({
        onClick { onClick() }
        attr("data-testid", "tab-$label")
        style {
            padding(Spacing.md, Spacing.xxl)
            fontSize(Typography.base)
            fontWeight("500")
            backgroundColor(if (isActive) Color(colors.surface) else Color("transparent"))
            color(if (isActive) Color(colors.primary) else Color(colors.textMuted))
            border(0.px)
            property("border-bottom", if (isActive) "2px solid ${colors.primary}" else "2px solid transparent")
            cursor("pointer")
            property("transition", "all 0.2s")
        }
    }) { Text(label) }
}

@Composable
private fun MenuButton(text: String, onClick: () -> Unit) {
    val colors = AppTheme.colors
    Button({
        onClick { onClick() }
        style {
            display(DisplayStyle.Block)
            width(100.percent)
            padding(Spacing.md, Spacing.lg)
            fontSize(Typography.base)
            fontWeight("500")
            textAlign("left")
            backgroundColor(Color("transparent"))
            color(Color(colors.text))
            border(0.px)
            cursor("pointer")
            property("transition", "background-color 0.2s")
        }
        onMouseOver { it.target.asDynamic().style.backgroundColor = colors.surfaceHover }
        onMouseOut { it.target.asDynamic().style.backgroundColor = "transparent" }
    }) { Text(text) }
}

@Composable
private fun InfoRow(label: String, value: String) {
    val colors = AppTheme.colors
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            padding(Spacing.sm, 0.px)
            property("border-bottom", "1px solid ${colors.border}")
        }
    }) {
        Span({
            style {
                fontSize(Typography.base)
                color(Color(colors.textMuted))
                fontWeight("500")
            }
        }) {
            Text(label)
        }
        Span({
            style {
                fontSize(Typography.base)
                color(Color(colors.text))
                fontWeight("400")
            }
        }) {
            Text(value)
        }
    }
}

@Composable
private fun LabelAssignmentSection(
    audiobook: AudiobookDto,
    apiClient: ApiClient,
    onUpdate: () -> Unit
) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()
    val userRoleStr = window.localStorage.getItem("user_role") ?: "NONE"
    val canEditLabel = userRoleStr.uppercase() in setOf("ADMIN", "SUPER_ADMIN") ||
        window.localStorage.getItem("is_label_admin") == "true"

    var labels by remember { mutableStateOf<List<LabelDto>>(emptyList()) }
    var selectedLabelId by remember(audiobook.id) { mutableStateOf(audiobook.labelId) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        if (canEditLabel) {
            try { labels = apiClient.listLabels() } catch (_: Exception) {}
        }
    }

    Div({
        style {
            backgroundColor(Color(colors.surfaceHover))
            borderRadius(BorderRadius.lg)
            padding(Spacing.lg)
            themeTransition()
        }
    }) {
        H3({
            style {
                fontSize(Typography.lg)
                fontWeight("600")
                color(Color(colors.text))
                marginTop(0.px)
                marginBottom(Spacing.lg)
            }
        }) {
            Text("Label")
        }

        if (canEditLabel && labels.isNotEmpty()) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(Spacing.md)
                }
            }) {
                Select({
                    onInput { event ->
                        selectedLabelId = event.value?.toIntOrNull()
                    }
                    style {
                        padding(Spacing.sm)
                        borderRadius(BorderRadius.sm)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        fontSize(14.px)
                        minWidth(200.px)
                    }
                }) {
                    Option("", { if (selectedLabelId == null) selected() }) { Text("-- Select Label --") }
                    labels.forEach { label ->
                        Option(label.id.toString(), { if (selectedLabelId == label.id) selected() }) {
                            Text(label.name)
                        }
                    }
                }

                if (selectedLabelId != audiobook.labelId) {
                    Button({
                        onClick {
                            val newLabelId = selectedLabelId ?: return@onClick
                            scope.launch {
                                saving = true
                                error = null
                                try {
                                    apiClient.updateAudiobook(audiobook.id, UpdateAudiobookRequest(labelId = newLabelId))
                                    onUpdate()
                                } catch (e: Exception) {
                                    error = e.message ?: "Failed to update label"
                                }
                                saving = false
                            }
                        }
                        style {
                            padding(Spacing.sm, Spacing.md)
                            backgroundColor(Color(colors.primary))
                            color(Color.white)
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                            fontSize(13.px)
                        }
                    }) { Text(if (saving) "Saving..." else "Save") }
                }
            }

            if (error != null) {
                Div({ style { color(Color(colors.danger)); fontSize(12.px); marginTop(Spacing.sm) } }) { Text(error!!) }
            }
        } else {
            // Read-only display
            InfoRow("Label", audiobook.labelName ?: "Not assigned")
        }
    }
}
